/**
 * Ad attribution capture.
 *
 * When someone arrives from a Facebook ad the click carries identifiers in the
 * URL and in cookies that Meta's pixel drops. Those identifiers are what let
 * Meta match a conversion back to the ad that paid for it — without them the
 * campaign optimises blind.
 *
 * The catch: they live on the *landing* URL only. By the time the visitor has
 * scrolled, opened a modal and paid, the query string is long gone. So we grab
 * everything on first load, persist it, and attach it to every lead and
 * registration we write.
 */

const STORAGE_KEY = 'eduspire_attribution';

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  // Meta's dynamic-parameter names, useful when ads use {{ad.id}} macros.
  'campaign_id',
  'adset_id',
  'ad_id',
  'placement',
  'site_source_name',
];

/** Read a browser cookie by name. Returns null when absent. */
export function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[2]) : null;
}

/** Write a first-party cookie. Defaults to Meta's 90-day attribution window. */
function setCookie(name, value, days = 90) {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

/**
 * Meta's click ID cookie (`_fbc`) is normally written by the pixel, but the
 * pixel may be blocked or slow to load. Building it ourselves means a click ID
 * present in the URL is never lost.
 *
 * Format: fb.<subdomainIndex>.<creationTimeMs>.<fbclid>
 */
function ensureFbc(fbclid) {
  const existing = getCookie('_fbc');
  if (existing) return existing;
  if (!fbclid) return null;

  const fbc = `fb.1.${Date.now()}.${fbclid}`;
  setCookie('_fbc', fbc);
  return fbc;
}

/** Generate a random, URL-safe id. Used for event dedupe and lead ids. */
export function randomId(prefix = '') {
  const bytes = new Uint8Array(9);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  const body = Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('');
  return prefix ? `${prefix}_${body}` : body;
}

/**
 * Read attribution from the current URL + cookies and merge it into whatever
 * was stored on an earlier visit.
 *
 * First-touch wins for the campaign fields: if someone arrives from an ad,
 * leaves, and comes back directly a day later, the ad still gets the credit.
 * The click identifiers (fbclid/fbc) are refreshed on every visit because Meta
 * matches on the most recent click.
 */
export function captureAttribution() {
  if (typeof window === 'undefined') return {};

  let stored = {};
  try {
    stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    stored = {};
  }

  const params = new URLSearchParams(window.location.search);
  const incoming = {};

  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) incoming[key] = value.slice(0, 200);
  }

  const fbclid = params.get('fbclid');
  const fbc = ensureFbc(fbclid);
  const fbp = getCookie('_fbp');

  const isFirstTouch = !stored.firstSeenAt;

  const merged = {
    // ── First-touch: never overwritten once set ──
    ...stored,
    ...(isFirstTouch
      ? {
          firstSeenAt: new Date().toISOString(),
          landingPage: window.location.pathname + window.location.search,
          referrer: document.referrer || 'direct',
        }
      : {}),

    // ── Campaign fields: only fill gaps, so first touch keeps credit ──
    ...Object.fromEntries(
      Object.entries(incoming).filter(([key]) => !stored[key])
    ),

    // ── Latest-touch: always refreshed ──
    ...(fbclid ? { fbclid } : {}),
    ...(fbc ? { fbc } : {}),
    ...(fbp ? { fbp } : {}),
    lastSeenAt: new Date().toISOString(),
    visitorId: stored.visitorId || randomId('v'),
  };

  // A visitor who arrived with any Meta identifier is treated as paid social.
  if (!merged.channel) {
    if (fbclid || merged.utm_source === 'fb' || merged.utm_source === 'facebook') {
      merged.channel = 'meta_ads';
    } else if (merged.utm_source) {
      merged.channel = merged.utm_source;
    } else if (merged.referrer && merged.referrer !== 'direct') {
      merged.channel = 'referral';
    } else {
      merged.channel = 'direct';
    }
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // Private browsing with storage disabled — attribution degrades to
    // in-memory only for this page view, which is acceptable.
  }

  return merged;
}

/** Return the stored attribution without re-reading the URL. */
export function getAttribution() {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

/**
 * Flatten attribution into the subset worth writing onto a Firestore document.
 * Keeps records small and avoids storing anything we don't report on.
 */
export function attributionForRecord() {
  const a = getAttribution();
  return {
    channel: a.channel || 'direct',
    source: a.utm_source || null,
    medium: a.utm_medium || null,
    campaign: a.utm_campaign || null,
    campaignId: a.campaign_id || null,
    adsetId: a.adset_id || null,
    adId: a.ad_id || null,
    placement: a.placement || null,
    fbclid: a.fbclid || null,
    landingPage: a.landingPage || null,
    referrer: a.referrer || null,
    visitorId: a.visitorId || null,
  };
}
